/**
 * The home-page widget for the Not on the Website page, in two sizes (Ian,
 * 2026-10-09; mockup .claude/plans/revenue-assurance-home-widget-mockup.html).
 *
 * Medium: two halves of equal weight — the cars shoppers cannot see (red) and
 * the sold cars still listed (indigo) — each with its pair of numbers and
 * "since the previous run". It reads the one-row summary the audit band reads,
 * so the home page stays cheap. No chart: Ian, "graph is just an awkward line".
 *
 * Large: the same halves with Days dark between them and the page's two
 * stacked charts beneath; it reads the page's own data. The breakdown drawers
 * and the car lists stay on the page, behind "Open the page".
 *
 * Every figure is a fact from a saved run; the change line compares two saved
 * runs and never estimates.
 */
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"

import RunStackedChart, { StackedPoint } from "../audit/notOnWebsite/RunStackedChart"
import { useAuth } from "../../context/AuthContext"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { useIsMobile } from "../../hooks/useIsMobile"
import {
  NotOnWebsitePoint,
  NotOnWebsiteResponse,
  NotOnWebsiteSummary,
  formatSellingPrice,
  getNotOnWebsite,
  getNotOnWebsiteSummary,
} from "../../api/notOnWebsite"
import { THEME } from "../../config/theme"
import { parseUtcTimestamp } from "../../utils/parseUtcTimestamp"
import { WidgetSize } from "./homeWidgets"

const CRITICAL = THEME.problemDisplay.severityColors.critical
const SOLD = THEME.problemDisplay.soldListingAccent
const MUTED = THEME.colors.neutral.light.textSecondary
const INK = THEME.colors.neutral.light.textPrimary
const BORDER = THEME.colors.neutral.light.border
const NARROW_CHART_WIDTH = 400

interface Props {
  size: WidgetSize
  /** The home page's edit mode: the size switch and Remove show only then
   * (Ian, 2026-10-09), so a card is clean the rest of the time. */
  editing: boolean
  onResize: (size: WidgetSize) => void
  onRemove: () => void
}

/** The figures both sizes show, from whichever endpoint the size reads. */
interface Figures {
  domain: string | null
  takenAt: string | null
  cars: number
  noPriceCars: number
  dollars: number
  sold: number
  soldNoPrice: number
  soldDollars: number
  previous: NotOnWebsitePoint | null
}

function figuresFromSummary(summary: NotOnWebsiteSummary): Figures | null {
  if (summary.cars === null) return null
  return {
    domain: summary.website_domain,
    takenAt: summary.taken_at,
    cars: summary.cars,
    noPriceCars: summary.no_price_cars ?? 0,
    dollars: summary.selling_price_total ?? 0,
    sold: summary.sold_still_advertised ?? 0,
    soldNoPrice: summary.sold_no_price_cars ?? 0,
    soldDollars: summary.sold_selling_price_total ?? 0,
    previous: summary.previous,
  }
}

function figuresFromPage(page: NotOnWebsiteResponse): Figures | null {
  // The newest SAVED point, not the page's live figures: the page re-measures
  // its grace windows at read time, so a day after a run it counts more cars
  // than the run saved. A widget states the saved run's facts, so both sizes
  // agree with each other and with the chart's last point.
  const newest = page.history.length > 0 ? page.history[page.history.length - 1] : null
  if (newest === null) return null
  const previous = page.history.length > 1 ? page.history[page.history.length - 2] : null
  return {
    domain: page.website_domain,
    takenAt: newest.taken_at,
    cars: newest.cars,
    noPriceCars: newest.no_price_cars ?? page.no_price_cars,
    dollars: newest.selling_price_total,
    sold: newest.sold_still_advertised ?? page.sold_still_advertised,
    soldNoPrice: newest.sold_no_price_cars ?? page.sold_no_price_cars,
    soldDollars: newest.sold_selling_price_total ?? page.sold_selling_price_total,
    previous,
  }
}

/**
 * "Down 1 car, down $12,300" between two saved runs. Falls are the goal and
 * read green; rises read red; no movement reads muted. Null when there is no
 * previous run to compare against.
 */
export function describeChange(
  count: number, dollars: number, previousCount: number, previousDollars: number, unit: string,
): { text: string; color: string } | null {
  const countChange = count - previousCount
  const dollarChange = dollars - previousDollars
  if (countChange === 0 && Math.abs(dollarChange) < 1) {
    return { text: "No change", color: MUTED }
  }
  const parts: string[] = []
  if (countChange !== 0) {
    const n = Math.abs(countChange)
    parts.push(`${countChange < 0 ? "Down" : "Up"} ${n} ${n === 1 ? unit : `${unit}s`}`)
  }
  if (Math.abs(dollarChange) >= 1) {
    parts.push(`${dollarChange < 0 ? "down" : "up"} ${formatSellingPrice(Math.abs(dollarChange))}`)
  }
  const text = parts.join(", ")
  const fell = countChange < 0 || (countChange === 0 && dollarChange < 0)
  return { text: text.charAt(0).toUpperCase() + text.slice(1), color: fell ? THEME.problemDisplay.severityColors.expected : CRITICAL }
}

export default function NotOnWebsiteWidget({ size, editing, onResize, onRemove }: Props) {
  const { dealerGroupId, websiteDomain } = useDealerGroup()
  const { user } = useAuth()
  const isMobile = useIsMobile()
  // The widget follows the person's home store, as the page does (Ian,
  // 2026-10-09): a Dodge manager's card is Dodge's numbers. No home store =
  // the whole group.
  const homeStore = user?.rooftop?.slug ?? null
  const [figures, setFigures] = useState<Figures | null | undefined>(undefined)
  const [page, setPage] = useState<NotOnWebsiteResponse | null>(null)
  // Kept apart from the figures so the card is named after the dealer's site
  // (and the store) even before the first point exists.
  const [domain, setDomain] = useState<string | null>(null)
  const [storeLabel, setStoreLabel] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (dealerGroupId === null) return undefined
    let cancelled = false
    setFigures(undefined)
    setPage(null)
    setError(null)
    const load = size === "large"
      ? getNotOnWebsite(dealerGroupId, homeStore).then((next) => {
          if (cancelled) return
          setPage(next)
          setDomain(next?.website_domain ?? null)
          setStoreLabel(next?.rooftop_label ?? null)
          setFigures(next === null ? null : figuresFromPage(next))
        })
      : getNotOnWebsiteSummary(dealerGroupId, homeStore).then((summary) => {
          if (cancelled) return
          setDomain(summary.website_domain)
          setStoreLabel(summary.rooftop_label)
          setFigures(figuresFromSummary(summary))
        })
    load.catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load the widget")
    })
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, homeStore, size])

  // Named from the group switcher first, so the card never says "the
  // website" for a moment before its own data lands.
  const siteDomain = domain ?? websiteDomain ?? null
  const site = siteDomain || "the website"
  const storeSuffix = storeLabel ? ` · ${storeLabel}` : ""

  return (
    <section aria-label={`${storeLabel ? `${storeLabel} · ` : ""}Not on ${site}`} style={widgetStyle}>
      <div style={headStyle}>
        <h2 style={titleStyle}>{storeLabel ? `${storeLabel} · ` : ""}{siteDomain ?? "Not on the website"}</h2>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          {editing && (
            <>
              <SizeSwitch size={size} onResize={onResize} />
              <button type="button" onClick={onRemove} style={quietButtonStyle}>Remove</button>
            </>
          )}
          <Link to="/inventory/audit/not-on-website" style={openLinkStyle}>Open the page →</Link>
        </div>
      </div>

      {dealerGroupId === null ? (
        <Note>Pick a dealer group to see its figures.</Note>
      ) : error !== null ? (
        <Note color={THEME.colors.semantic.danger}>{error}</Note>
      ) : figures === undefined ? (
        <Note>Loading…</Note>
      ) : figures === null ? (
        <Note>The first point lands with the next audit run.</Note>
      ) : (
        <>
          <div style={halvesStyle(isMobile, size === "large")}>
            <Half
              accent={CRITICAL}
              label={`Not on ${site}${storeSuffix}`}
              count={figures.cars}
              countCaption={["cars for sale", "that shoppers can't find"]}
              dollars={figures.dollars}
              dollarsCaption="HomeNet Selling Price"
              noPrice={figures.noPriceCars}
              change={figures.previous
                ? describeChange(figures.cars, figures.dollars, figures.previous.cars, figures.previous.selling_price_total, "car")
                : null}
              previousDay={figures.previous ? parseUtcTimestamp(figures.previous.taken_at) : null}
            />
            {size === "large" && page && (
              <div style={{ ...halfStyle(CRITICAL), borderLeft: isMobile ? "none" : `1px solid ${BORDER}` }}>
                <div style={labelStyle}>Days dark avg.</div>
                <div style={numberStyle(CRITICAL)}>
                  {page.days_dark_avg === null ? "—" : Math.round(page.days_dark_avg).toLocaleString()}
                </div>
                <div style={captionStyle}>
                  {page.days_dark_oldest === null || page.days_dark_youngest === null ? (
                    "no cars dark"
                  ) : (
                    <>
                      oldest <b style={{ color: CRITICAL }}>{page.days_dark_oldest}</b> days
                      <br />
                      youngest <b style={{ color: CRITICAL }}>{page.days_dark_youngest}</b> days
                    </>
                  )}
                </div>
              </div>
            )}
            <Half
              accent={SOLD}
              label={`Sold, still on ${site}${storeSuffix}`}
              count={figures.sold}
              countCaption={["listings for cars", "you no longer have"]}
              dollars={figures.soldDollars}
              dollarsCaption="advertised on the site"
              noPrice={figures.soldNoPrice}
              change={figures.previous && figures.previous.sold_still_advertised !== null && figures.previous.sold_selling_price_total !== null
                ? describeChange(figures.sold, figures.soldDollars, figures.previous.sold_still_advertised, figures.previous.sold_selling_price_total, "listing")
                : null}
              previousDay={figures.previous ? parseUtcTimestamp(figures.previous.taken_at) : null}
              divided={!isMobile}
            />
          </div>

          {/* The charts are a desktop thing; on a phone the large size keeps
              the halves and Days dark and leaves the charts to the page
              (Ian, 2026-10-09). */}
          {size === "large" && !isMobile && page && <LargeCharts page={page} site={site} storeSuffix={storeSuffix} isMobile={false} />}

          <div style={footStyle}>
            <span>
              From the audit run{" "}
              <b style={{ color: INK }}>{figures.takenAt ? formatRunTime(figures.takenAt) : "—"}</b>
            </span>
            <span>
              {size === "large" && !isMobile
                ? "The breakdowns and the car lists are on the page."
                : size === "large"
                  ? "The charts, the breakdowns and the car lists are on the page."
                  : "Days dark, the breakdowns, the charts and the car lists are on the page."}
            </span>
          </div>
        </>
      )}
    </section>
  )
}

function Half({
  accent, label, count, countCaption, dollars, dollarsCaption, noPrice, change, previousDay, divided = false,
}: {
  accent: string
  label: string
  count: number
  countCaption: [string, string]
  dollars: number
  dollarsCaption: string
  noPrice: number
  change: { text: string; color: string } | null
  previousDay: Date | null
  divided?: boolean
}) {
  return (
    <div style={{ ...halfStyle(accent), ...(divided && { borderLeft: `1px solid ${BORDER}` }) }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
        <div>
          <div style={numberStyle(accent)}>{count.toLocaleString()}</div>
          <div style={captionStyle}>
            {countCaption[0]}
            <br />
            {countCaption[1]}
          </div>
        </div>
        <div>
          <div style={numberStyle(accent)}>{formatSellingPrice(dollars, true)}</div>
          <div style={captionStyle}>
            {dollarsCaption}
            {noPrice > 0 && (
              <>
                <br />
                {noPrice} with no price, not counted
              </>
            )}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12.5, marginTop: 10, color: MUTED }}>
        {change === null ? (
          "No previous run to compare against yet"
        ) : (
          <>
            <b style={{ color: change.color }}>{change.text}</b> since the previous run
            {previousDay ? `, ${previousDay.toLocaleDateString([], { month: "short", day: "numeric" })}` : ""}
          </>
        )}
      </div>
    </div>
  )
}

function LargeCharts({
  page, site, storeSuffix, isMobile,
}: {
  page: NotOnWebsiteResponse
  site: string
  storeSuffix: string
  isMobile: boolean
}) {
  const darkPoints: StackedPoint[] = page.history.map((point) => ({
    date: parseUtcTimestamp(point.taken_at), dollars: point.selling_price_total, count: point.cars,
  }))
  const soldPoints: StackedPoint[] = page.history.map((point) => ({
    date: parseUtcTimestamp(point.taken_at), dollars: point.sold_selling_price_total, count: point.sold_still_advertised,
  }))
  const alertsStartedAt = page.alerts_started_at ? parseUtcTimestamp(page.alerts_started_at) : null
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 2fr) minmax(0, 1fr)",
        borderTop: `1px solid ${BORDER}`,
      }}
    >
      <div>
        <div style={chartHeadStyle}>
          <span>Cars not on {site}{storeSuffix}, and what they are worth</span>
          <span style={{ fontSize: 11.5, fontWeight: 400, color: MUTED }}>per audit run · last 60 days</span>
        </div>
        <RunStackedChart
          points={darkPoints}
          color={CRITICAL}
          dollarsLabel="HomeNet Selling Price of those cars"
          countLabel={`Cars not on ${site}`}
          countUnit="cars"
          formatDollars={(value) => formatSellingPrice(value, true)}
          ariaLabel={`Selling price of cars not on ${site} per audit run`}
          alertsStartedAt={alertsStartedAt}
          emptyText="No runs saved yet."
          viewWidth={isMobile ? NARROW_CHART_WIDTH : undefined}
        />
      </div>
      <div style={{ borderLeft: isMobile ? "none" : `1px solid ${BORDER}`, borderTop: isMobile ? `1px solid ${BORDER}` : "none" }}>
        <div style={chartHeadStyle}>
          <span>Sold, still on {site}{storeSuffix}</span>
          <span style={{ fontSize: 11.5, fontWeight: 400, color: MUTED }}>per audit run</span>
        </div>
        <RunStackedChart
          points={soldPoints}
          color={SOLD}
          dollarsLabel={`What ${site} is advertising them at`}
          countLabel="Listings for cars you no longer have"
          countUnit="listings"
          formatDollars={(value) => formatSellingPrice(value, true)}
          ariaLabel={`Sold cars still on ${site}, per audit run`}
          alertsStartedAt={alertsStartedAt}
          emptyText="No runs saved yet."
          viewWidth={NARROW_CHART_WIDTH}
        />
      </div>
    </div>
  )
}

function SizeSwitch({ size, onResize }: { size: WidgetSize; onResize: (size: WidgetSize) => void }) {
  return (
    <span role="group" aria-label="Widget size" style={{ display: "inline-flex", gap: 4 }}>
      {(["medium", "large"] as const).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={size === option}
          onClick={() => onResize(option)}
          style={{
            ...quietButtonStyle,
            color: size === option ? INK : MUTED,
            textDecoration: size === option ? "underline" : "none",
          }}
        >
          {option === "medium" ? "Medium" : "Large"}
        </button>
      ))}
    </span>
  )
}

function Note({ children, color = MUTED }: { children: React.ReactNode; color?: string }) {
  return <div style={{ padding: "18px 16px", fontSize: 13, color }}>{children}</div>
}

function formatRunTime(timestamp: string): string {
  return parseUtcTimestamp(timestamp).toLocaleString([], {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  })
}

const widgetStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${BORDER}`,
  borderRadius: THEME.borderRadius.lg,
  overflow: "hidden",
  fontFamily: THEME.fonts.primary,
}

const headStyle: React.CSSProperties = {
  padding: "12px 16px",
  borderBottom: `1px solid ${BORDER}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 10,
  flexWrap: "wrap",
}

const titleStyle: React.CSSProperties = { margin: 0, fontSize: 15, fontWeight: 600, color: INK }

const openLinkStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: THEME.problemDisplay.severityColors.info,
  fontWeight: 600,
  textDecoration: "none",
  whiteSpace: "nowrap",
}

const quietButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: THEME.fonts.primary,
  color: MUTED,
  cursor: "pointer",
}

function halvesStyle(isMobile: boolean, withDays: boolean): React.CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : withDays ? "2fr 1fr 2fr" : "1fr 1fr",
  }
}

function halfStyle(accent: string): React.CSSProperties {
  return {
    padding: "14px 16px 12px",
    borderTop: `3px solid ${accent}`,
  }
}

const labelStyle: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: MUTED,
  fontWeight: 600,
}

function numberStyle(color: string): React.CSSProperties {
  return {
    fontSize: 24,
    fontWeight: 600,
    letterSpacing: "-0.8px",
    lineHeight: 1.15,
    marginTop: 4,
    color,
    fontVariantNumeric: "tabular-nums",
  }
}

const captionStyle: React.CSSProperties = { fontSize: 11, color: MUTED, marginTop: 3, lineHeight: 1.45 }

const chartHeadStyle: React.CSSProperties = {
  padding: "12px 16px 0",
  fontSize: 13,
  fontWeight: 600,
  color: INK,
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
}

const footStyle: React.CSSProperties = {
  padding: "8px 16px 12px",
  fontSize: 11.5,
  color: MUTED,
  borderTop: `1px solid ${BORDER}`,
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
}
